import { Fragment } from 'react';

export const DATA_GRID_ROW_TYPE = {
  CUSTOM: 'customRow',
  INLINE: 'inline',
};

/**
 * DataGrid renders a structured label–value layout using columns and rows.
 *
 * The grid supports three row types:
 * 1. Standard rows (label + value or custom render)
 * 2. Inline rows (multiple label–value pairs in a single row)
 * 3. Custom rows (full control via render escape hatch)
 */

/**
 * @typedef {Object} DataGridItem
 * @property {string} label - Label text shown on the left
 * @property {*} [value] - Value shown on the right (ignored if render is provided)
 * @property {() => JSX.Element} [render] - Optional custom renderer for the value
 * @property {'start'|'center'|'end'} [labelAlignment] - Alignment of the label with respect to the value/custom rendered component
 *
 * @typedef {Object} InlineRow
 * @property {'inline'} type - Inline row type
 * @property {Array<{ label: string, value: *, labelAlignment: 'start'|'center'|'end' }>} items - Inline label–value pairs
 *
 * @typedef {Object} CustomRow
 * @property {'customRow'} type - Custom row type
 * @property {() => JSX.Element} render - Full custom row renderer
 *
 * @typedef {Object} DataGridColumn
 * @property {Array<DataGridItem | InlineRow | CustomRow>} rows - Rows in this column
 *
 * @param {Object} props
 * @param {Array<DataGridColumn>} props.columns - Columns configuration
 */
export default function DataGrid({ columns = [] }) {
  return (
    <div className='grid grid--2-cols grid--with-divider'>
      {columns.map((column, columnIdx) => (
        <div key={columnIdx} className='data-grid-column'>
          {column.rows.map((row, rowIdx) => {
            // Full custom escape hatch
            if (row.type === DATA_GRID_ROW_TYPE.CUSTOM && row.render)
              return <Fragment key={rowIdx}>{row.render()}</Fragment>;

            // Inline grouped rows
            if (row.type === DATA_GRID_ROW_TYPE.INLINE) {
              return (
                <div key={rowIdx} className='row row--between col-2'>
                  {row.items.map((item, itemIdx) => (
                    <div key={itemIdx} className='row gap-5'>
                      <span className={`label--secondary self-${item.labelAlignment || 'center'}`}>
                        {item.label}
                      </span>
                      <span className='value'>{item.value}</span>
                    </div>
                  ))}
                </div>
              );
            }

            // Standard label value row
            return (
              <div key={rowIdx} className='data-grid-row'>
                <span className={`label--secondary self-${row.labelAlignment || 'start'}`}>
                  {row.label}
                </span>
                <span className='value justify-self-end'>
                  {row.render ? row.render() : row.value}
                </span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
